import * as React from "react";
import { ChevronDown } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { cn } from "@/lib/cn";
import { useViewport } from "@/lib/useViewport";

/**
 * Sub-nav with tab families (MV2-847, app/TemplateDetail §2).
 *
 * Fourteen tabs stop being a menu when they are grouped by the job:
 * Performance · Equipment · Money · Work & admin. Each family carries a 9px
 * eyebrow; the active tab is gray-900/500 with a 2px `--action` underline.
 * A tab the persona cannot have is DISABLED with its reason — never hidden,
 * except the three hide-list tabs (Connectivity, Controls for owners, MLOps),
 * which the caller simply omits.
 */
export interface SubNavTab {
  to: string;
  label: string;
  /** Match only the exact path (react-router `end`). */
  end?: boolean;
  /**
   * Force the active state. `NavLink` matches on the pathname only, so a tab
   * set that lives in a query parameter (`?tab=skill`, MLOps site page) tells
   * the nav which tab is on. Omit to keep the pathname match.
   */
  active?: boolean;
  disabled?: boolean;
  /** Required when `disabled`. */
  reason?: string;
  /** Small count or chip after the label. */
  badge?: React.ReactNode;
}

export interface SubNavFamily {
  /** Omit for a flat tab row. */
  label?: string;
  tabs: SubNavTab[];
}

export function SubNav({
  families,
  more,
  className,
}: {
  families: SubNavFamily[];
  /**
   * MV2-1233 — tabs opened rarely enough to sit behind a "More" menu at the
   * end of the row (desktop); a phone's chip row keeps them inline.
   */
  more?: SubNavTab[];
  className?: string;
}): React.ReactElement {
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The More menu names the tab you are on when it is one of its own.
  const moreActive = (more ?? []).find((t) => t.active ?? pathname.split("/").includes(t.to));

  // MV2-985 — mobile: a flat row of rounded chips, no family eyebrows or
  // dividers (five tabs don't need grouping the way fourteen do), active
  // tab solid-filled rather than underlined — comfortable to tap and to
  // tell apart at a glance while scanning, not reading, on a phone.
  if (isMobile) {
    const tabs = [...families.flatMap((f) => f.tabs), ...(more ?? [])];
    return (
      <nav aria-label="Sections" className={cn("flex gap-1.5 overflow-x-auto pb-2 [scrollbar-width:none]", className)}>
        {tabs.map((t) =>
          t.disabled ? (
            <span
              key={t.to}
              role="tab"
              aria-disabled="true"
              title={t.reason}
              className="flex-none cursor-not-allowed whitespace-nowrap rounded-full bg-[color:var(--gray-100)] px-3.5 py-1.5 text-[13px] text-gray-400"
            >
              {t.label}
            </span>
          ) : (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              role="tab"
              aria-current={t.active ? "page" : undefined}
              className={({ isActive }) =>
                cn(
                  "flex-none whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors",
                  (t.active ?? isActive)
                    ? "bg-[color:var(--action)] text-[color:var(--action-fg)]"
                    : "bg-[color:var(--gray-100)] text-gray-600 active:bg-[color:var(--gray-200)]",
                )
              }
            >
              {t.label}
              {t.badge}
            </NavLink>
          ),
        )}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Sections"
      className={cn(
        "flex items-end overflow-x-auto [scrollbar-width:none]",
        className,
      )}
    >
      {families.map((f, gi) => (
        <div
          key={f.label ?? gi}
          className={cn(
            "flex flex-col",
            gi > 0 && "ml-2.5 border-l border-gray-200 pl-2.5",
          )}
        >
          {f.label && (
            <div className="type-eyebrow px-3 text-[9px]">{f.label}</div>
          )}
          <div className="flex">
            {f.tabs.map((t) =>
              t.disabled ? (
                <span
                  key={t.to}
                  role="tab"
                  aria-disabled="true"
                  title={t.reason}
                  className="relative cursor-not-allowed whitespace-nowrap px-3 py-[7px] text-[13.5px] text-gray-500 opacity-45"
                >
                  {t.label}
                </span>
              ) : (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  role="tab"
                  aria-current={t.active ? "page" : undefined}
                  className={({ isActive }) =>
                    cn(
                      "relative flex items-center gap-1.5 whitespace-nowrap px-3 py-[7px] text-[13.5px] transition-colors duration-hover",
                      (t.active ?? isActive)
                        ? "font-medium text-gray-900 after:absolute after:-bottom-px after:left-3 after:right-3 after:h-[2px] after:rounded-t-[2px] after:bg-action"
                        : "text-gray-500 hover:text-gray-900",
                    )
                  }
                >
                  {t.label}
                  {t.badge}
                </NavLink>
              ),
            )}
          </div>
        </div>
      ))}
      {more && more.length > 0 && (
        <div className="ml-2.5 flex border-l border-gray-200 pl-2.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  "relative flex items-center gap-1 whitespace-nowrap px-3 py-[7px] text-[13.5px] outline-none transition-colors duration-hover focus-visible:ring-2 focus-visible:ring-focus",
                  moreActive
                    ? "font-medium text-gray-900 after:absolute after:-bottom-px after:left-3 after:right-3 after:h-[2px] after:rounded-t-[2px] after:bg-action"
                    : "text-gray-500 hover:text-gray-900",
                )}
              >
                {moreActive ? moreActive.label : "More"} <ChevronDown size={13} aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {more.map((t) => (
                <DropdownMenuItem key={t.to} disabled={t.disabled} title={t.disabled ? t.reason : undefined} onSelect={() => navigate(t.to)}>
                  {t.label}
                  {t.badge}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </nav>
  );
}
